import { useOption } from '@mui/base';
import cn from 'classnames';
import { forwardRef, useContext, useRef } from 'react';
import { mergeRefs } from 'react-merge-refs';

import { ListboxItem } from '../../internal';
import { SelectContext } from '../select/select-context';

import type { ForwardedRef, ReactElement } from 'react';

import type { SelectOptionComponentType, SelectOptionProps } from './select-option.types';

/**
 * The `SelectOption` component is used to define the options of the `Select` or `SelectField` components.
 *
 * ### Usage
 *
 * ```tsx
 * import { Select, SelectOption } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomSelect() {
 *   return (
 *    <Select>
 *      <SelectOption value="option-1">Option 1</SelectOption>
 *      <SelectOption value="option-2">Option 2</SelectOption>
 *    </Select>
 *   );
 * }
 * ```
 */
const SelectOption = forwardRef(
  <OptionValue,>(
    props: SelectOptionProps<OptionValue>,
    forwardedRef: ForwardedRef<HTMLLIElement> | null,
  ): ReactElement => {
    const { children, className, disabled = false, label, value, ...rest } = props;

    const optionRef = useRef<HTMLElement>(null);
    const combinedRef = mergeRefs([optionRef, forwardedRef]);

    // If `label` is not explicitly provided, the `children` are used for convenience.
    // This is used to populate the select's trigger with the selected option's label.
    const computedLabel = label ?? (typeof children === 'string' ? children : optionRef.current?.textContent?.trim());

    const { getRootProps, highlighted, rootRef, selected } = useOption({
      disabled,
      label: computedLabel,
      rootRef: combinedRef,
      value,
    });

    const rootProps = getRootProps();

    const selectContext = useContext(SelectContext);
    if (!selectContext) {
      throw new Error('SelectOption must be used within a SelectContext');
    }
    const { multiple } = selectContext;
    const checkType = multiple ? 'checkbox' : 'checkmark';

    return (
      <ListboxItem
        {...rootProps}
        checkType={checkType}
        className={cn('rbui-select-option', className)}
        disabled={disabled}
        highlighted={highlighted}
        ref={rootRef}
        selected={selected}
        {...rest}
      >
        {children}
      </ListboxItem>
    );
  },
);

SelectOption.displayName = 'SelectOption';

export default SelectOption as SelectOptionComponentType;
